/**
 * P2d WILL / WON'T contract renderer (design spec §04): a DETERMINISTIC pure
 * function from profile fields to the living document's contract lines. No
 * model involvement — the sentence table below is constants, table-driven
 * tested. The M2 capability card may later reuse the same table (spec §04
 * leaves it a seat; P2d does not touch M2).
 */

/** The subset of a published/merged profile view the contract derives from. */
export interface ContractInput {
  /** Author-written WILL lines (win over derivation when present). */
  will?: string[];
  /** Union-only prohibitions, listed verbatim. */
  wont: string[];
  /** Effective skill ids (built-in + ext:{tenant}/{name}). */
  skills: string[];
  /** kbFilter sources ([] = unrestricted). */
  kbSources: string[];
  /** CNY budget caps (P2c) — rendered as WON'T lines when set. */
  budget?: { daily?: number; perExecution?: number };
  /** visibility.minClearance ("internal" renders a WON'T line). */
  minClearance?: string;
}

export interface ContractView {
  will: string[];
  wont: string[];
}

/** Sentence constants for skill-derived WILL lines (§04: 常量不是生成). */
const SKILL_WILL_SENTENCES: Record<string, (input: ContractInput) => string> = {
  search_kb: (input) =>
    input.kbSources.length > 0 ? `用「${input.kbSources.join("、")}」的资料答疑` : "用企业知识库答疑",
  code_qa: () => "以代码仓库为准回答技术问题",
  create_ticket: () => "答不上来时建工单转人工",
  web_search: () => "需要时联网检索公开信息，并注明来源",
};

/** ext:{tenant}/{name} → the human name segment. */
function extSkillName(id: string): string {
  const slash = id.indexOf("/");
  return slash >= 0 ? id.slice(slash + 1) : id;
}

/** Derive the contract lines. Pure; same input → same lines, always. */
export function deriveContract(input: ContractInput): ContractView {
  // WILL: the author's own lines win; otherwise one line per member item.
  let will: string[];
  if (input.will !== undefined && input.will.length > 0) {
    will = [...input.will];
  } else {
    will = [];
    for (const skill of input.skills) {
      const sentence = SKILL_WILL_SENTENCES[skill];
      if (sentence !== undefined) {
        will.push(sentence(input));
      } else if (skill.startsWith("ext:")) {
        will.push(`使用行业技能「${extSkillName(skill)}」`);
      }
    }
  }

  // WON'T: verbatim prohibitions + derived budget/visibility lines.
  const wont = [...input.wont];
  if (input.budget?.daily !== undefined) {
    wont.push(`单日花费不超过 ¥${input.budget.daily}`);
  }
  if (input.budget?.perExecution !== undefined) {
    wont.push(`单次回答花费不超过 ¥${input.budget.perExecution}`);
  }
  if (input.minClearance === "internal") {
    wont.push("只对内部用户可见");
  }
  return { will, wont };
}
